import { __decorate } from "tslib";
import Component from "vue-class-component";
import Task from "@/services/task";
import ReactiveComponent from "@/components/ReactiveComponent";
import Formatter from "@/utils/formatter";
import { User_Status2_Enum } from "@/generated/graphql";
import TemplateContent from "@/components/template/TemplateContent.vue";
import authService from "@/services/authService";
import userService from "@/services/userService";
import { Watch } from "vue-property-decorator";
var Column;
(function (Column) {
    Column["name"] = "name";
    Column["email"] = "email";
    Column["board"] = "boards";
    Column["role"] = "role";
    Column["status"] = "status";
    Column["action"] = "action";
})(Column || (Column = {}));
var Status;
(function (Status) {
    Status["active"] = "active";
    Status["invited"] = "invited";
    Status["disabled"] = "disabled";
})(Status || (Status = {}));
let Users = class Users extends ReactiveComponent {
    constructor() {
        super(...arguments);
        this.tdClass = "users-datatable";
        this.fields = [
            {
                key: "name_normalized",
                label: "Name",
                sortable: true,
                tdClass: this.tdClass
            },
            { key: "email", label: "Email", sortable: true, tdClass: this.tdClass },
            { key: "boards", label: "Boards", sortable: true, tdClass: this.tdClass },
            { key: "role", label: "Role", sortable: true, tdClass: this.tdClass },
            { key: "status", label: "Status", sortable: true, tdClass: this.tdClass },
            {
                key: "actions",
                label: "Actions",
                sortable: false,
                tdClass: this.tdClass
            }
        ];
        this.filter = null;
        this.sessionUserId = -1;
        this.usersQuery = { user: [] };
        this.usersTask = Task.idle();
        this.updateStatusTask = Task.idle();
        this.deleteTask = Task.idle();
        this.resendInvitationEmailTask = Task.idle();
        this.status = 'active';
    }
    created() {
        this.subscribe("session", authService.validSession, (session) => {
            this.sessionUserId = session.userId;
        });
        this.subscribeQuery({
            tag: "allUsers",
            options: {
                query: require("@/apollo/queries/allUsers.graphql")
            },
            onTask: task => {
                this.usersTask = task;
                if (task.isSuccessful) {
                    this.usersQuery = task.data;
                }
            }
        });
    }
    onUpdateQuery() {
        let status = null;
        if (this.$route.query.status != null) {
            status = Status[this.$route.query.status.toString()];
        }
        this.status = status != null ? status : Status.active;
    }
    get users() {
        return this.usersQuery.user.filter(u => u.status2 === this.status);
    }
    formatStatus(status) {
        return Formatter.formatUserStatus(status).toLowerCase();
    }
    formatRole(role) {
        return Formatter.formatUserRole(role).toLowerCase();
    }
    formatToggleStatus(status) {
        return status == "disabled" ? "Enable" : "Disable";
    }
    formatBoards(user) {
        return Formatter.formatBoards(user.users_boards.map(it => it.board));
    }
    statusBadgeClass(status) {
        let color;
        switch (status) {
            case "invited":
                color = "blue";
                break;
            case "active":
                color = "green";
                break;
            case "disabled":
            default:
                color = "grey";
                break;
        }
        return `badge--${color}`;
    }
    roleBadgeClass(role) {
        let color;
        switch (role) {
            case "member":
                return "kt-badge--dark kt-badge--outline";
            case "coach":
                color = "red";
                break;
            case "staff":
            default:
                color = "yellow";
                break;
        }
        return `badge--${color}`;
    }
    async toggleStatus(userId) {
        const user = this.users.find(it => it.id === userId);
        if (user == undefined) {
            return;
        }
        const message = user.status2 == User_Status2_Enum.Active
            ? `Disable ${user.firstname} ${user.lastname}?`
            : `Enable ${user.firstname} ${user.lastname}?`;
        const result = await this.$bvModal.msgBoxConfirm(message, {
            size: "sm",
            buttonSize: "md",
            okVariant: "danger",
            okTitle: this.formatToggleStatus(user.status2),
            cancelTitle: "Cancel",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
        });
        if (result !== true) {
            return;
        }
        const newStatus = user.status2 == User_Status2_Enum.Active
            ? User_Status2_Enum.Disabled
            : User_Status2_Enum.Active;
        const allUsersQuery = require("@/apollo/queries/allUsers.graphql");
        this.subscribeMutation({
            tag: "updateStatus",
            options: {
                mutation: require("@/apollo/mutations/updateUserStatus.graphql"),
                variables: {
                    id: user.id,
                    status: newStatus
                },
                update: (store, { data }) => {
                    try {
                        const allUsers = store.readQuery({
                            query: allUsersQuery
                        });
                        const user = data.update_user.returning[0];
                        const index = allUsers.user.findIndex(it => it.id == user.id);
                        if (index >= 0) {
                            allUsers.user[index].status2 = user.status2;
                        }
                        store.writeQuery({ query: allUsersQuery, data: allUsers });
                    }
                    catch (e) {
                        console.log("Failed to update cache after update_user mutation");
                    }
                },
                optimisticResponse: {
                    __typename: "mutation_root",
                    update_user: {
                        __typename: "user_mutation_response",
                        returning: [{ __typename: "user", id: user.id, status2: newStatus }]
                    }
                }
            },
            onTask: task => (this.updateStatusTask = task)
        });
    }
    async deleteUser(userId) {
        const user = this.users.find(it => it.id === userId);
        if (user == undefined) {
            return;
        }
        const result = await this.$bvModal.msgBoxConfirm(`Delete ${user.firstname} ${user.lastname}?\nThis action is definitive`, {
            size: "sm",
            buttonSize: "md",
            okVariant: "danger",
            okTitle: "Delete",
            cancelTitle: "Cancel",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
        });
        if (result !== true) {
            return;
        }
        const allUsersQuery = require("@/apollo/queries/allUsers.graphql");
        this.subscribeMutation({
            tag: "deleteUser",
            options: {
                mutation: require("@/apollo/mutations/deleteUser.graphql"),
                variables: {
                    id: user.id
                },
                update: (store, { data }) => {
                    try {
                        const allUsers = store.readQuery({
                            query: allUsersQuery
                        });
                        if (data.delete_user.affected_rows != 1) {
                            return;
                        }
                        const index = allUsers.user.findIndex(it => it.id == user.id);
                        if (index < 0) {
                            return;
                        }
                        allUsers.user.splice(index, 1);
                        store.writeQuery({ query: allUsersQuery, data: allUsers });
                    }
                    catch (e) {
                        console.log("Failed to update cache after delete_user mutation");
                    }
                },
                optimisticResponse: {
                    __typename: "mutation_root",
                    delete_user: {
                        __typename: "user_mutation_response",
                        affected_rows: 1
                    }
                }
            },
            onTask: task => (this.deleteTask = task)
        });
    }
    async resendInvitationEmail(userId) {
        const user = this.users.find(it => it.id === userId);
        if (user == undefined) {
            return;
        }
        const result = await this.$bvModal.msgBoxConfirm(`Resend invite for ${user.firstname} ${user.lastname} at ${user.email} ?`, {
            size: "sm",
            buttonSize: "md",
            // okVariant: "danger",
            okTitle: "Send",
            cancelTitle: "Cancel",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
        });
        if (result !== true) {
            return;
        }
        this.resendInvitationEmailTask = Task.loading();
        const success = await userService.resendInvitationEmail(user.id);
        this.resendInvitationEmailTask = success
            ? Task.success(null)
            : Task.failure("Resending invitation email failed");
    }
};
__decorate([
    Watch("$route.query", { immediate: true })
], Users.prototype, "onUpdateQuery", null);
Users = __decorate([
    Component({
        components: { TemplateContent }
    })
], Users);
export default Users;
